import { useEffect, useMemo, useState } from "react";
import { usePaginationState } from "../../App/Components/Table/State/PaginationState";
import { useArchiveGroupsMutation, useFilteredGroups } from "../../App/Queries/GroupQueries";
import { createPaginationQueryString } from "../../App/Queries/queryHelpers";
import { useNotification } from "../../App/State/Hooks/notificationHooks";
import { useDebouncedHandler } from "../../App/State/Hooks/useDebouncedHandler";
import { useGroupListingStore } from "../../App/State/Stores/GroupListingStore";
import { IPaginationQuery } from "../../App/Typings/MessageText";

export const MaxSearchLength = 100;

export type CandidateGroupsState = ReturnType<typeof useCandidateGroupState>;

/**
 * @description creates and maintains the state of the CandidateGroup page
 * @returns {CandidateGroupsState}
 */
export function useCandidateGroupState() {
  const { displaySuccess, displayError } = useNotification();

  //#region local state
  const [archivingGroupId, setCurrentlyArchivingGroupId] = useState<string>("");
  const [isConfirmArchiveOpen, setIsConfirmArchiveOpen] = useState<boolean>(false);
  const [isCreatingGroup, setIsCreatingGroup] = useState<boolean>(false);
  const [isCreatingGroupError, setIsCreatingGroupError] = useState<boolean>(false);
  const [isArchiving, setIsArchiving] = useState<boolean>(false);
  const paginationState = usePaginationState({ sortBy: "name", sortDirection: "asc" }, "groups");
  const [searchInput, setSearchInput] = useState<string>(paginationState.getters.search);
  const [searchInputError, setSearchInputError] = useState<string>("");
  const { view, setView } = useGroupListingStore();
  //#endregion

  const paginationQuery = useMemo(
    () =>
      ({
        page: paginationState.getters.page + 1,
        pageSize: paginationState.getters.pageSize,
        sortBy: paginationState.getters.sortBy,
        sortDirection: paginationState.getters.sortDirection,
        search: paginationState.getters.search,
      }) as IPaginationQuery,
    [
      paginationState.getters.page,
      paginationState.getters.pageSize,
      paginationState.getters.sortBy,
      paginationState.getters.sortDirection,
      paginationState.getters.search,
    ],
  );

  const {
    data: groupsPagination,
    isLoading,
    isError,
    error,
  } = useFilteredGroups(createPaginationQueryString(paginationQuery));

  if (isError || groupsPagination?.error) {
    console.error(error ?? groupsPagination?.error);
  }

  const handleChangeSortBy = (sortBy: string) => {
    paginationState.handlers.handleRequestSort(sortBy);
  };

  const handleChangeSortDirection = (sortDirection: "asc" | "desc") => {
    paginationState.setters.setSortDirection(sortDirection);
    paginationState.setters.setPage(0);
  };

  const handleSearchFilter = useDebouncedHandler((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value.length > MaxSearchLength) {
      setSearchInputError(`Search input must be less than or equal to ${MaxSearchLength} characters`);
    } else {
      setSearchInputError("");
    }
    setSearchInput(e.target.value);
  }, 300);

  useEffect(() => {
    if (searchInput.length <= MaxSearchLength && searchInput !== paginationState.getters.search) {
      paginationState.handlers.handleSearchChange({
        target: { value: searchInput },
      } as React.ChangeEvent<HTMLInputElement>);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput, paginationState.getters.search]);

  const { mutateAsync: archiveGroups } = useArchiveGroupsMutation();

  const handleConfirmArchiveCancel = async () => {
    setIsConfirmArchiveOpen(false);
    setTimeout(() => {
      setCurrentlyArchivingGroupId("");
    }, 300);
  };

  const handleConfirmArchiveClose = async () => {
    if (!archivingGroupId || archivingGroupId === "") {
      displayError("Error archiving group");
      return;
    }
    setIsArchiving(true);
    await archiveGroups([archivingGroupId], {
      onError: () => {
        displayError("Error archiving group");
        setIsArchiving(false);
      },
      onSuccess: () => {
        displaySuccess("Group archived");
        setIsConfirmArchiveOpen(false);
        setIsArchiving(false);
      },
    });
  };

  const handleArchiveGroup = async (groupId: string) => {
    setCurrentlyArchivingGroupId(groupId);
    setIsConfirmArchiveOpen(true);
  };

  const handleToggleCreateGroup = () => setIsCreatingGroup(!isCreatingGroup);

  const handleChangeView = (view: "card" | "list") => {
    localStorage.removeItem("groups-custom-table-state");
    setView(view);
  };

  return {
    getters: {
      archivingGroupId,
      groupsPagination,
      isConfirmArchiveOpen,
      isCreatingGroup,
      isCreatingGroupError,
      isArchiving,
      isLoading,
      paginationState,
      searchInputError,
      view,
    },
    handlers: {
      handleChangeSortBy,
      handleChangeSortDirection,
      handleConfirmArchiveCancel,
      handleConfirmArchiveClose,
      handleArchiveGroup,
      handleSearchFilter,
      handleToggleCreateGroup,
      handleChangeView,
    },
    setters: {
      setIsCreatingGroup,
      setIsCreatingGroupError,
    },
  };
}
